import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Palette, Plus, RefreshCw, X } from "lucide-react";
import type { ExtractedBrandVoice } from "./types";

export type BrandVoiceSectionProps = {
  brandVoice: ExtractedBrandVoice | null;
  existingBrandName: string | null | undefined;
  applied: boolean;
  discarded: boolean;
  isPending: boolean;
  onApply: (voice: ExtractedBrandVoice) => void;
  onDiscard: () => void;
};

export function BrandVoiceSection({
  brandVoice,
  existingBrandName,
  applied,
  discarded,
  isPending,
  onApply,
  onDiscard,
}: BrandVoiceSectionProps) {
  if (!brandVoice || discarded) return null;
  const hasContent =
    brandVoice.brandName ||
    brandVoice.suggestedTone ||
    brandVoice.wordsToUse?.length ||
    brandVoice.slogan ||
    brandVoice.catchPhrases?.length ||
    brandVoice.emojis?.length;
  if (!hasContent) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Palette className="w-4 h-4 text-indigo-600" />
        <h4 className="font-semibold text-sm">Voz da marca detectada</h4>
      </div>
      <Card className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xs space-y-1.5 flex-1 min-w-0">
            {brandVoice.brandName && <p><strong>Marca:</strong> {brandVoice.brandName}</p>}
            {brandVoice.suggestedTone && <p><strong>Tom sugerido:</strong> {brandVoice.suggestedTone}</p>}
            {brandVoice.slogan && (
              <p><strong>Slogan:</strong> <span className="text-indigo-700">"{brandVoice.slogan}"</span></p>
            )}
            {brandVoice.catchPhrases?.length ? (
              <div>
                <strong>Frases de efeito:</strong>
                <ul className="ml-3 mt-0.5 space-y-0.5">
                  {brandVoice.catchPhrases.slice(0, 5).map((p, i) => (
                    <li key={i} className="italic text-muted-foreground">"{p}"</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {brandVoice.emojis?.length ? (
              <p>
                <strong>Emojis:</strong> <span className="text-base">{brandVoice.emojis.slice(0, 12).join(" ")}</span>
              </p>
            ) : null}
            {brandVoice.wordsToUse?.length ? (
              <p><strong>Palavras a usar:</strong> {brandVoice.wordsToUse.slice(0, 8).join(", ")}</p>
            ) : null}
            {brandVoice.rationale && (
              <p className="italic text-muted-foreground mt-1">"{brandVoice.rationale}"</p>
            )}
          </div>
          {applied ? (
            <Badge variant="default" className="shrink-0 bg-emerald-100 text-emerald-700 border-emerald-200">
              <Check className="w-3 h-3 mr-1" /> Mesclado
            </Badge>
          ) : (
            <div className="flex gap-1 shrink-0">
              <Button
                size="sm"
                variant="default"
                className="h-7 px-2"
                onClick={() => onApply(brandVoice)}
                disabled={isPending}
              >
                {existingBrandName ? (
                  <><RefreshCw className="w-3 h-3 mr-1" /> Mesclar</>
                ) : (
                  <><Plus className="w-3 h-3 mr-1" /> Aplicar</>
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground"
                title="Descartar sugestão"
                onClick={onDiscard}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </Card>
      {existingBrandName && !applied && (
        <p className="text-[10px] text-muted-foreground mt-1">
          ✓ A voz da marca já está configurada — <strong>mesclar</strong> irá agregar palavras e frases novas sem sobrescrever as existentes.
        </p>
      )}
    </div>
  );
}
